<!--
  "Comment" beside a text selection in the document.

  Selecting words in the document -- with a mouse, or with the keyboard in
  caret browsing (Shift+arrows) -- shows a small Comment button at the end of
  the selection. Choosing it hands the selection, already turned into what the
  server's anchor accepts (lib/anchors.ts), to the page, which opens the
  sidebar's composer with the quote showing.

  THE KEYBOARD. A floating button is not in the natural tab order after the
  caret, so while one is showing:
    * Tab (from the document, not from a control) moves focus straight to it;
    * Ctrl+Alt+M (Cmd+Option+M on a Mac, Google Docs' shortcut) comments on
      the selection directly;
    * a polite status line says both, once, when it appears;
    * Escape dismisses it.
  Focusing the button does not disturb the document's selection, and the
  button swallows mousedown for the same reason.

  It reads the selection on `selectionchange`, which covers every way a
  selection is made, and is positioned inside the document column so it
  scrolls with the text rather than floating over the page.

  A selection inside a rendered diagram, which has no text blocks, is tried
  second (lib/selection.ts `diagramTarget`): it comments on the label selected
  in, or on the whole diagram. TASK-045.
-->
<script lang="ts">
  import { untrack } from 'svelte'
  import { anchorForDiagram, anchorForSelection, type AnchorDraft, type TextBlock } from '../anchors'
  import { diagramTarget, selectionTarget } from '../selection'

  let {
    root,
    blocks,
    oncomment,
  }: {
    /** The element the document's blocks render into. */
    root: HTMLElement | null
    /** The document's text blocks, in the server's scan order. */
    blocks: TextBlock[]
    oncomment: (draft: AnchorDraft) => void
  } = $props()

  let draft = $state<AnchorDraft | null>(null)
  let pos = $state({ top: 0, left: 0 })
  let status = $state('')
  let button = $state<HTMLButtonElement | null>(null)
  let wrap = $state<HTMLElement | null>(null)

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  const shortcut = isMac ? '⌘⌥M' : 'Ctrl+Alt+M'

  let frame = 0
  function schedule() {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(read)
  }

  function read() {
    const sel = document.getSelection()
    // Focus on our own button keeps the draft: the selection is unchanged.
    if (button && document.activeElement === button) return
    if (!root || !sel || sel.rangeCount === 0 || sel.isCollapsed) {
      hide()
      return
    }
    const range = sel.getRangeAt(0)
    const target = selectionTarget(root, range)
    // Not in any text block: perhaps wholly inside a rendered diagram (TASK-045).
    const inDiagram = target ? null : diagramTarget(root, range)
    const next = target
      ? anchorForSelection(blocks, target.blockId, target.start, target.end, target.clipped)
      : inDiagram
        ? anchorForDiagram(blocks, inDiagram.blockId, inDiagram.label, inDiagram.display)
        : null
    if (!next) {
      hide()
      return
    }
    const appeared = !draft
    draft = next
    place(range)
    if (appeared) status = `Selection can be commented on: press Tab, then Enter, or ${shortcut}.`
  }

  function place(range: Range) {
    const host = wrap?.offsetParent as HTMLElement | null
    if (!host) return
    const rects = Array.from(range.getClientRects()).filter((r) => r.width > 0 || r.height > 0)
    const last = rects[rects.length - 1] ?? range.getBoundingClientRect()
    const box = host.getBoundingClientRect()
    const width = 104
    pos = {
      top: last.bottom - box.top + 6,
      left: Math.max(0, Math.min(last.right - box.left - width / 2, box.width - width)),
    }
  }

  function hide() {
    draft = null
    status = ''
  }

  function choose() {
    if (!draft) return
    const chosen = draft
    hide()
    oncomment(chosen)
  }

  function onkeydown(e: KeyboardEvent) {
    if (!draft) return
    if (e.code === 'KeyM' && e.altKey && (e.ctrlKey || e.metaKey) && !e.shiftKey) {
      e.preventDefault()
      choose()
      return
    }
    if (e.key === 'Escape' && (document.activeElement === button || root?.contains(document.activeElement))) {
      hide()
      return
    }
    if (e.key === 'Tab' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      const active = document.activeElement
      const fromDocument =
        !active || active === document.body || (root?.contains(active) && !active.closest('a,button,[tabindex]'))
      if (fromDocument && button) {
        e.preventDefault()
        button.focus()
      }
    }
  }

  $effect(() => {
    document.addEventListener('selectionchange', schedule)
    window.addEventListener('resize', schedule)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('selectionchange', schedule)
      window.removeEventListener('resize', schedule)
    }
  })

  // The blocks changing under a live selection (an SSE update) re-reads it:
  // the offsets it maps to may have moved.
  $effect(() => {
    void blocks
    // Untracked: `read` replaces the draft, and depending on it here would
    // re-read on every frame for as long as a selection stood.
    if (untrack(() => draft)) schedule()
  })
</script>

<svelte:window {onkeydown} />

<div class="selection-comment-host" bind:this={wrap}>
  {#if draft}
    <button
      type="button"
      class="btn btn-quiet btn-small selection-comment"
      style={`top:${pos.top}px;left:${pos.left}px`}
      aria-keyshortcuts="Control+Alt+M Meta+Alt+M"
      bind:this={button}
      onmousedown={(e) => e.preventDefault()}
      onclick={choose}
      onblur={() => schedule()}
    >
      Comment
    </button>
  {/if}
  <p class="visually-hidden" role="status">{status}</p>
</div>

<style>
  /* The host is empty and in flow; its offsetParent (the document column) is
     what the button is placed against. */
  .selection-comment-host {
    position: static;
    height: 0;
  }

  /* Ink on paper, lifted off the text: the red is for the primary act in the
     composer, not for a button that appears on every selection. */
  .selection-comment {
    position: absolute;
    z-index: 5;
    width: 6.5rem;
    background: var(--paper);
    border-color: var(--color-ink-soft);
    box-shadow: 0 4px 14px color-mix(in oklab, var(--ink) 18%, transparent);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    margin: 0;
  }
</style>
